import React, { useState, useEffect } from "react";
import "./SurveyModal.css";
import { fetchSurveyQuestions } from "../setupSurvey";

function SurveyModal({ onComplete, onClose }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fallback questions if database fetch fails
  const fallbackQuestions = [
    {
      id: "role",
      question_text: "Which best describes your current role?",
      landing_survey_options: [
        { id: "1", option_text: "Student (University / College / Bootcamp)" },
        {
          id: "2",
          option_text: "Aspiring Developer (Self-taught / Career Changer)",
        },
        { id: "3", option_text: "Professional Software Engineer / Developer" },
        { id: "4", option_text: "Data Scientist / Analyst" },
      ],
    },
    {
      id: "reason",
      question_text:
        "What is your main reason for learning Data Structures & Algorithms?",
      landing_survey_options: [
        { id: "5", option_text: "Coding interviews / job placements" },
        { id: "6", option_text: "Skill improvement for work or projects" },
        { id: "7", option_text: "Competitive programming" },
        { id: "8", option_text: "Academic / coursework support" },
        { id: "9", option_text: "Personal growth / curiosity" },
      ],
    },
    {
      id: "experience",
      question_text: "How would you describe your current DSA experience?",
      landing_survey_options: [
        { id: "10", option_text: "Just starting out (new to DSA)" },
        {
          id: "11",
          option_text: "Know the basics (arrays, loops, simple sorting)",
        },
        {
          id: "12",
          option_text: "Comfortable with intermediate concepts (trees, graphs)",
        },
        {
          id: "13",
          option_text:
            "Experienced (LeetCode, coding contests, real problem-solving)",
        },
      ],
    },
  ];

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuestions = async () => {
    try {
      const dbQuestions = await fetchSurveyQuestions();
      if (dbQuestions && dbQuestions.length > 0) {
        setQuestions(dbQuestions);
      } else {
        console.warn("No questions found in database, using fallback");
        setQuestions(fallbackQuestions);
      }
    } catch (error) {
      console.error("Error loading questions:", error);
      setQuestions(fallbackQuestions);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (option) => {
    const currentQ = questions[currentQuestion];

    // Store answer with question_id and option_id
    const newAnswer = {
      question_id: currentQ.id,
      option_id: option.id,
      question_text: currentQ.question_text,
      option_text: option.option_text,
    };

    const newAnswers = [...answers, newAnswer];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    } else {
      setTimeout(() => {
        onComplete(newAnswers);
      }, 300);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <p>Loading survey...</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <p>Unable to load survey. Please try again later.</p>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const currentQ = questions[currentQuestion];
  const selectedOptionId = answers.find(
    (a) => a.question_id === currentQ.id
  )?.option_id;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className="question-counter">
          Question {currentQuestion + 1} of {questions.length}
        </div>

        <h2 className="question-title">{currentQ.question_text}</h2>

        <div className="options-container">
          {currentQ.landing_survey_options.map((option, index) => (
            <button
              key={option.id}
              className={`option-btn ${
                selectedOptionId === option.id ? "selected" : ""
              }`}
              onClick={() => handleAnswer(option)}
            >
              <span className="option-number">{index + 1}</span>
              <span className="option-text">{option.option_text}</span>
              <span className="option-check">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M16.6667 5L7.50004 14.1667L3.33337 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SurveyModal;
